import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { formatNameFromEmail } from '../../../ActionPlan/utils/actionPlanTransformers';
import { getPriorityDisplayText, getSourceDisplayText, getTypeDisplayText } from '../../../ActionPlan/utils/formOptions';

// Import logo
const warehouseNavigatorLogo = require('../../../../assets/warehouse_navigator.png');

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatDateToDDMMYYYY = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Escape HTML special characters for safe rendering.
 * Works in both browser and non-browser environments.
 */
const escapeHtml = (text: string): string => {
  const div = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (div) {
    div.textContent = text;
    return div.innerHTML;
  }

  return text.replace(/[&<>"']/g, (m) =>
    (
      {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      } as const
    )[m] || m,
  );
};

const TABLE_COLUMNS = ['Title', 'Assigned User', 'Status', 'Target Date', 'Source'] as const;

export interface TopOpportunityItem {
  ID: number;
  TITLE: string;
  SOURCE: string;
  TYPE: string;
  PRIORITY: string;
  STATUS: string;
  ASSIGNMENT_STATUS?: string;
  OWNER_GPID: string;
  OWNER_ROLE?: string;
  ASSIGNEE_GPID: string;
  OWNER_NAME?: string;
  ASSIGNEE_NAME?: string;
  TARGET_DATE?: string;
  START_DATE?: string;
  CREATED_AT?: string;
  UPDATED_AT?: string;
  DESCRIPTION?: string;
  INVOLVED_EMPLOYEE_GPIDS?: string;
  INVOLVED_EMPLOYEE_NAMES?: string;
  ACTION_PLAN_DESCRIPTION?: string;
  [key: string]: any;
}

export interface TopOpportunitiesPagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
}

interface TopOpportunityExpansionData {
  actionPlanOwner: string;
  type: string;
  startDate: string;
  source: string;
  frontlineEmployee: string;
  actionPlan: string;
}

interface TopOpportunityPrintRow {
  id: string;
  title: string;
  assignedUser: string;
  status: string;
  targetDate: string;
  source: string;
  badgeText?: string;
  priority: string;
  expansionData: TopOpportunityExpansionData;
}

interface TopOpportunitiesTableProps {
  data: TopOpportunityItem[];
  isLoading?: boolean;
  onViewDetailsPress?: (item: TopOpportunityItem) => void;
  error?: string;
  onFavoriteToggle?: (itemId: string) => void;
  onStatusChange?: (itemId: string, newStatus: string) => Promise<void>;
  maxRecordsToShow?: number;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export interface TopOpportunitiesTablePrintRef {
  print: () => void;
}

// ============================================================================
// HTML TEMPLATE RENDERING
// ============================================================================

const renderStyles = (): string => {
  return `
    @page {
      size: landscape;
      margin: 0.5in 0.25in;
    }

    * {
      box-sizing: border-box;
    }

    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      color: #1C1C1C;
      font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      background: #FFFFFF;
    }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    thead {
      display: table-header-group;
    }

    tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .print-table {
      width: 100%;
    }

    .print-header-cell {
      padding: 0 0 10px 0;
      border-bottom: 0;
    }

    .header-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border: 1px solid #DADADA;
      border-radius: 6px;
      padding: 8px 12px;
      background: #FFFFFF;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .logo {
      width: 20px;
      height: 20px;
      object-fit: contain;
      border-radius: 4px;
      flex-shrink: 0;
    }

    .header-title {
      font-size: 16px;
      font-weight: 700;
      color: #093F6E;
      white-space: nowrap;
    }

    .header-date {
      font-size: 13px;
      font-weight: 400;
      color: #6B6B6B;
      white-space: nowrap;
    }

    .section-title {
      font-size: 16px;
      font-weight: 700;
      color: #4B4B4B;
      padding: 10px 0 8px 10px;
      text-align: left;
    }

    .table-head-row th {
      font-size: 8px;
      font-weight: 600;
      text-transform: uppercase;
      color: #000000;
      text-align: left;
      padding: 10px 12px;
      background: #FFFFFF;
      border-bottom: 1px solid #E6E6E6;
    }

    .table-head-row th:nth-child(n+2) {
      text-align: right;
    }

    .summary-row td {
      padding: 12px;
      border-bottom: 0;
      vertical-align: top;
    }

    .title-cell {
      font-size: 12px;
      font-weight: 700;
      color: #1C1C1C;
      word-break: break-word;
      text-transform: capitalize;
    }

    .pill {
      display: inline-block;
      font-size: 9px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 999px;
      margin-left: 8px;
      vertical-align: middle;
    }

    .pill--new {
      background: #106E98;
      color: #FFFFFF;
    }

    .assigned-cell, .source-cell, .date-cell {
      font-size: 12px;
      font-weight: 600;
      color: #1C1C1C;
      white-space: nowrap;
      text-align: right;
    }

    .status-cell {
      white-space: nowrap;
      text-align: right;
    }

    .status-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      color: #FFFFFF;
      background: #6B7280;
    }

    .status-badge--completed { background: #1F7A4D; }
    .status-badge--complete { background: #1F7A4D; }
    .status-badge--done { background: #1F7A4D; }
    .status-badge--overdue { background: #C12C01; }
    .status-badge--in-progress { background: #106E98; }
    .status-badge--new { background: #106E98; }

    .expanded-row td {
      padding: 0 12px 12px 12px;
      border-bottom: 1px solid #E6E6E6;
    }

    .expanded-box {
      padding-top: 6px;
    }

    .expanded-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      gap: 14px;
      margin-top: 6px;
    }

    .field {
      min-width: 0;
    }

    .field-label {
      font-size: 8px;
      text-transform: uppercase;
      color: #6B6B6B;
      margin-bottom: 4px;
    }

    .field-value {
      font-size: 10px;
      color: #1C1C1C;
      word-break: break-word;
    }

    .action-plan {
      margin-top: 10px;
    }

    .action-plan .field-value {
      font-size: 10px;
      color: #4B4B4B;
      line-height: 1.35;
    }

    /* Keep each opportunity block together if possible */
    tbody.opp-block {
      page-break-inside: avoid;
      break-inside: avoid;
    }
  `;
};

const renderPrintHeader = (opts: { date: string; logoBase64?: string }): string => {
  const logoImg = opts.logoBase64
    ? `<img class="logo" src="${opts.logoBase64}" alt="Warehouse Navigator" />`
    : `<div class="logo" style="background: transparent;"></div>`;

  return `
    <tr>
      <th class="print-header-cell" colspan="5">
        <div class="header-bar">
          <div class="header-left">
            ${logoImg}
            <span class="header-title">Warehouse Navigator</span>
          </div>
          <span class="header-date">${escapeHtml(opts.date)}</span>
        </div>
      </th>
    </tr>
  `;
};

const renderSectionTitle = (title: string): string => {
  return `
    <tr>
      <th colspan="5" style="padding:0; border:0; text-align:left;">
        <div class="section-title">${escapeHtml(title)}</div>
      </th>
    </tr>
  `;
};

const renderTableHeader = (): string => {
  return `
    <tr class="table-head-row">
      ${TABLE_COLUMNS.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}
    </tr>
  `;
};

type StatusBadgeInfo = {
  text: string;
  backgroundColor: string;
};

const normalizeStatusKey = (status: string): string => {
  return String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_');
};

const getStatusBadgeInfo = (status: string): StatusBadgeInfo => {
  const key = normalizeStatusKey(status);

  switch (key) {
    case 'completed':
    case 'complete':
    case 'done':
      return { text: 'Completed', backgroundColor: 'rgb(0, 133, 83)' };

    case 'in_progress':
    case 'inprogress':
      return { text: 'In Progress', backgroundColor: 'rgb(49, 107, 184)' };

    case 'overdue':
      return { text: 'Overdue', backgroundColor: 'rgb(204, 110, 22)' };

    case 'declined':
      return { text: 'Declined', backgroundColor: '#C12C01' };

    default:
      return { text: status || '', backgroundColor: '#6B7280' };
  }
};

const renderTableRow = (item: TopOpportunityPrintRow): string => {
  const statusText = item.status || '';
  const badge = getStatusBadgeInfo(statusText);
  const badgeHtml = item.badgeText
    ? `<span class="pill pill--new">${escapeHtml(item.badgeText)}</span>`
    : '';

  return `
    <tr class="summary-row">
      <td class="title-cell">${escapeHtml(item.title)}${badgeHtml}</td>
      <td class="assigned-cell">${escapeHtml(item.assignedUser)}</td>
      <td class="status-cell"><span class="status-badge" style="background: ${escapeHtml(
        badge.backgroundColor,
      )};">${escapeHtml(badge.text)}</span></td>
      <td class="date-cell">${escapeHtml(item.targetDate)}</td>
      <td class="source-cell">${escapeHtml(item.source)}</td>
    </tr>
  `;
};

const renderExpandedRow = (item: TopOpportunityPrintRow): string => {
  const fields: Array<{ label: string; value: string }> = [
    { label: 'Action Plan Owner', value: item.expansionData.actionPlanOwner || '--' },
    { label: 'Type', value: item.expansionData.type || '--' },
    { label: 'Start Date', value: item.expansionData.startDate || '--' },
    { label: 'Source', value: item.expansionData.source || '--' },
    { label: 'Frontline Employee', value: item.expansionData.frontlineEmployee || '--' },
  ];

  return `
    <tr class="expanded-row">
      <td colspan="5">
        <div class="expanded-box">
          <div class="expanded-grid">
            ${fields
              .map(
                (f) => `
                  <div class="field">
                    <div class="field-label">${escapeHtml(f.label)}</div>
                    <div class="field-value">${escapeHtml(f.value)}</div>
                  </div>
                `,
              )
              .join('')}
          </div>
          <div class="action-plan">
            <div class="field-label">Action Plan</div>
            <div class="field-value">${escapeHtml(item.expansionData.actionPlan || '--')}</div>
          </div>
        </div>
      </td>
    </tr>
  `;
};

export const generateTopOpportunitiesTableHTML = (args: {
  date: string;
  logoBase64?: string;
  items: TopOpportunityPrintRow[];
  isLoading?: boolean;
  error?: string;
}): string => {
  const headerHtml = `
    <thead>
      ${renderPrintHeader({ date: args.date, logoBase64: args.logoBase64 })}
      ${renderSectionTitle('Top Opportunities')}
      ${renderTableHeader()}
    </thead>
  `;

  const bodyHtml = (() => {
    if (args.isLoading) {
      return `
        <tbody>
          <tr>
            <td colspan="5" style="padding: 14px 12px; color:#6B6B6B; font-size: 12px;">Loading…</td>
          </tr>
        </tbody>
      `;
    }

    if (args.error) {
      return `
        <tbody>
          <tr>
            <td colspan="5" style="padding: 14px 12px; color:#C12C01; font-size: 12px;">Error: ${escapeHtml(
              args.error,
            )}</td>
          </tr>
        </tbody>
      `;
    }

    if (!args.items || args.items.length === 0) {
      return `
        <tbody>
          <tr>
            <td colspan="5" style="padding: 14px 12px; color:#6B6B6B; font-size: 12px;">No top opportunities found</td>
          </tr>
        </tbody>
      `;
    }

    return args.items
      .map(
        (item) => `
          <tbody class="opp-block">
            ${renderTableRow(item)}
            ${renderExpandedRow(item)}
          </tbody>
        `,
      )
      .join('');
  })();

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      ${renderStyles()}
    </style>
  </head>
  <body>
    <table class="print-table">
      <colgroup>
        <col style="width: 52%;" />
        <col style="width: 12%;" />
        <col style="width: 12%;" />
        <col style="width: 12%;" />
        <col style="width: 12%;" />
      </colgroup>
      ${headerHtml}
      ${bodyHtml}
    </table>
  </body>
</html>`;
};

const TopOpportunitiesTablePrint = forwardRef(function TopOpportunitiesTablePrint(
  props: TopOpportunitiesTableProps,
  ref: React.Ref<TopOpportunitiesTablePrintRef>,
) {
  const { data, isLoading, error } = props;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [logoBase64, setLogoBase64] = useState<string>('');

  // Load logo on mount (web only)
  useEffect(() => {
    const loadLogo = async () => {
      if (Platform.OS === 'web') {
        try {
          const response = await fetch(warehouseNavigatorLogo);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoBase64(reader.result as string);
          };
          reader.readAsDataURL(blob);
        } catch (e) {
          console.warn('Failed to load logo for print:', e);
          setLogoBase64('');
        }
      }
    };

    loadLogo();
  }, []);

  const printDate = useMemo(
    () =>
      new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    [],
  );

  // Preserve data transformation logic from existing implementation
  const printItems: TopOpportunityPrintRow[] = useMemo(() => {
    if (isLoading || error || !data || data.length === 0) return [];
    return data.map((item) => ({
      id: String(item.ID),
      title: item.TITLE || '',
      assignedUser: formatNameFromEmail((item as any).ASSIGNEE_EMAIL, item.ASSIGNEE_GPID),
      status:
        item.ASSIGNMENT_STATUS?.toLowerCase() === 'declined'
          ? item.ASSIGNMENT_STATUS || ''
          : item.STATUS || '',
      priority: getPriorityDisplayText(item.PRIORITY) || '',
      targetDate: formatDateToDDMMYYYY(item.TARGET_DATE),
      source: getSourceDisplayText(item.SOURCE) || '',
      badgeText: item.STATUS === 'New' ? 'NEW' : undefined,
      expansionData: {
        actionPlanOwner: formatNameFromEmail((item as any).OWNER_EMAIL, item.OWNER_GPID),
        type: getTypeDisplayText(item.TYPE) || '',
        startDate: formatDateToDDMMYYYY(item.START_DATE),
        source: getSourceDisplayText(item.SOURCE) || '',
        frontlineEmployee: item?.INVOLVED_EMPLOYEE_NAMES || '',
        actionPlan: item.ACTION_PLAN_DESCRIPTION || '',
      },
    }));
  }, [data, error, isLoading]);

  const htmlContent = useMemo(
    () =>
      generateTopOpportunitiesTableHTML({
        date: printDate,
        logoBase64,
        items: printItems,
        isLoading,
        error,
      }),
    [printDate, logoBase64, printItems, isLoading, error],
  );

  useImperativeHandle(
    ref,
    () => ({
      print: () => {
        if (Platform.OS === 'web') {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
          return;
        }

        // For mobile, use react-native-print or react-native-html-to-pdf
        // Example: RNPrint.print({ html: htmlContent });
        console.log('Mobile print - use react-native-print with HTML:', htmlContent);
      },
    }),
    [htmlContent],
  );

  // Update iframe content when HTML changes
  useEffect(() => {
    if (Platform.OS === 'web' && iframeRef.current?.contentDocument) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(htmlContent);
      doc.close();
    }
  }, [htmlContent]);

  if (Platform.OS !== 'web') {
    // For mobile, return empty view - printing is handled via HTML string
    return <View style={styles.hiddenContainer} />;
  }

  return (
    <View style={styles.hiddenContainer}>
      {/* @ts-ignore - iframe is web-only */}
      <iframe
        ref={iframeRef}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
        }}
        title="Top Opportunities Table Print"
      />
    </View>
  );
});

const styles = StyleSheet.create({
  hiddenContainer: {
    position: 'absolute',
    left: -9999,
    top: -9999,
    width: 1,
    height: 1,
    opacity: 0,
  },
});

TopOpportunitiesTablePrint.displayName = 'TopOpportunitiesTablePrint';

// Make this component web-compatible for print
(TopOpportunitiesTablePrint as any).PureComponent = true;

export default TopOpportunitiesTablePrint;
